import React, { useState, useEffect } from "react";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import axios from "axios";
import { Info, History, Star, UserPlus, Edit } from "lucide-react";
import { EditableText } from "./editabletext";
// Import logo (dopasuj ścieżkę do swojej struktury projektu)
const logoImg = "./logo_PTH.png";

const adres = "https://api.pth-hokej.pl";

// --- ANIMACJE ---
const pulseAnim = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

// --- GLOBALNE STYLE ---
const AboutGlobalStyle = createGlobalStyle`
  body {
    background-color: #ffffff;
    color: #111;
    margin: 0;
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  }
`;

// --- STYLED COMPONENTS ---

const PageLoader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  
  img {
    height: 80px;
    filter: grayscale(100%);
    margin-bottom: 20px;
    animation: ${pulseAnim} 1.5s infinite ease-in-out;
  }

  span {
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 3px;
    font-size: 1.2rem;
    color: #111;
  }
`;

const AboutContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 5% 100px 5%;
  background-color: #ffffff;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding-bottom: 60px;
  }
`;

const PageHeader = styled.header`
  padding: 150px 0 60px 0;
  border-bottom: 2px solid #111;
  margin-bottom: 60px;
  animation: ${slideUp} 0.6s ease-out forwards;

  @media (max-width: 768px) {
    padding: 120px 0 40px 0;
    margin-bottom: 40px;
  }

  @media (max-width: 480px) {
    padding: 100px 0 30px 0;
    margin-bottom: 30px;
  }

  h1 {
    font-size: clamp(3rem, 8vw, 6rem);
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: -3px;
    margin: 0;
    line-height: 0.9;
    
    span {
       color: #888;
       display: block;
    }

    @media (max-width: 480px) {
      letter-spacing: -1.5px; /* Lżejszy kerning dla czytelności na micro ekranach */
    }
  }
`;

const SectionRow = styled.section`
  display: flex;
  gap: 80px;
  padding: 80px 0;
  border-bottom: 1px solid #eee;

  @media (max-width: 968px) {
    flex-direction: column;
    gap: 30px;
    padding: 60px 0;
  }

  @media (max-width: 480px) {
    padding: 40px 0;
  }
`;

const SectionHeader = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: flex-start;

  h2 {
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 900;
    text-transform: uppercase;
    margin: 0;
    line-height: 1.1;
    letter-spacing: -1px;
    color: #111;
  }

  svg {
    width: 48px;
    height: 48px;
    color: #ccc;
  }

  @media (max-width: 968px) {
    flex-direction: row;
    align-items: center;

    svg {
      width: 36px;
      height: 36px;
    }
  }

  @media (max-width: 480px) {
    gap: 15px;

    svg {
      width: 30px;
      height: 30px;
      flex-shrink: 0; /* Zapobiega zgniataniu ikony przy długich tytułach */
    }
  }
`;

const SectionContent = styled.div`
  flex: 2;
  font-size: 1.15rem;
  line-height: 1.8;
  color: #555;
  white-space: pre-wrap; /* Odczytuje entery/nowe linie z bazy danych */
  overflow-wrap: break-word; /* Zapobiega rozjeżdżaniu siatki przez długie słowa/linki */
  word-wrap: break-word;
  
  @media (max-width: 768px) {
    font-size: 1.05rem;
    line-height: 1.7;
  }
`;

const CTASection = styled.section`
  background-color: #111;
  color: #fff;
  padding: 80px 5%;
  margin-top: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 30px;
  transition: transform 0.3s ease;
  box-sizing: border-box;

  &:hover {
    transform: translateY(-5px);
  }

  @media (max-width: 768px) {
    padding: 60px 5%;
    margin-top: 60px;
  }

  @media (max-width: 480px) {
    padding: 40px 5%;
    margin-top: 40px;
    gap: 20px;
  }

  svg {
    width: 50px;
    height: 50px;
    color: #fff;
    
    @media (max-width: 480px) {
      width: 40px;
      height: 40px;
    }
  }

  h2 {
     font-size: clamp(2rem, 5vw, 4rem);
     font-weight: 900;
     text-transform: uppercase;
     margin: 0;
     letter-spacing: -1px;
  }
  
  .cta-text {
     font-size: 1.15rem;
     line-height: 1.8;
     color: #ccc;
     max-width: 700px;
     white-space: pre-wrap;
     overflow-wrap: break-word;

     @media (max-width: 768px) {
        font-size: 1rem;
     }
  }
`;

const ContactButton = styled.a`
  margin-top: 20px;
  padding: 18px 50px;
  background: #fff;
  color: #000;
  text-decoration: none;
  text-transform: uppercase;
  font-weight: bold;
  font-size: 1rem;
  letter-spacing: 2px;
  transition: all 0.3s;
  border: 2px solid #fff;
  display: inline-block;

  &:hover {
    background: #000;
    color: #fff;
  }

  @media (max-width: 480px) {
    width: 100%; /* Przycisk na całą szerokość na telefonach */
    box-sizing: border-box;
    text-align: center;
    padding: 15px 20px;
    font-size: 0.9rem;
    margin-top: 10px;
  }
`;

// --- KOMPONENT GŁÓWNY ---
// --- KOMPONENT GŁÓWNY ---

export const AboutUs = () => {
  const [isLoading, setIsLoading] = useState(true);

  // 1. ZARZĄDZANIE LOADEREM
  useEffect(() => {
    // Ponieważ teksty pobierają się same w komponencie EditableText,
    // tutaj musimy tylko upewnić się, że pobrano zdjęcie logo, a potem wyłączamy ekran ładowania.
    const img = new Image();
    img.src = logoImg;
    img.onload = () => setIsLoading(false);
    img.onerror = () => setIsLoading(false);
  }, []);

  // Ekran ładowania
  if (isLoading) {
    return (
      <PageLoader>
        <img src={logoImg} alt="Logo PTH Koziołki" />
        <span>Wczytywanie...</span>
      </PageLoader>
    );
  }

  return (
    <>
      <AboutGlobalStyle />
      <AboutContainer>

        {/* NAGŁÓWEK */}
        <PageHeader>
          <h1>
            {/* Część 1: PTH Koziołki */}
            <EditableText textId="naglowek_aboutus_main" defaultText="PTH Koziołki">
              <h1></h1>
            </EditableText>

            {/* Część 2: O Klubie (zachowuje stylizację koloru i nowej linii) */}
            <EditableText textId="naglowek_aboutus_sub" defaultText="O Klubie">
              <span></span>
            </EditableText>
          </h1>
        </PageHeader>

        <SectionRow>
          <SectionHeader>
            <Star />
            <EditableText textId="kimjestesmy" defaultText="Kim jesteśmy">
              <h2></h2>
            </EditableText>
          </SectionHeader>
          <SectionContent>
            <EditableText textId="kimjestesmy_tresc" defaultText="Jesteśmy klubem hokejowym z pasją.">
              <p></p>
            </EditableText>
          </SectionContent>
        </SectionRow>

        <SectionRow>
          <SectionHeader>
            <History />
            <EditableText textId="naszahistoria" defaultText="Nasza historia">
              <h2></h2>
            </EditableText>
          </SectionHeader>
          <SectionContent>
            <EditableText textId="naszahistoria_tresc" defaultText="Klub powstał wiele lat temu z myślą o młodzieży.">
              <p></p>
            </EditableText>
          </SectionContent>
        </SectionRow>

        <SectionRow>
          <SectionHeader>
            <Info />
            <EditableText textId="naszewartosci_tytul2" defaultText="Nasze Wartości">
              <h2></h2>
            </EditableText>
          </SectionHeader>
          <SectionContent>
            <EditableText textId="naszewartosci_tresc2" defaultText="Wierzymy w fair play, dyscyplinę i zaangażowanie.">
              <p></p>
            </EditableText>
          </SectionContent>
        </SectionRow>

        {/* 4. JAK DOŁĄCZYĆ (CTA) - Teraz w pełni edytowalne! */}
        <CTASection>
          <UserPlus />
          <EditableText textId="jakdolaczyc_tytul" defaultText="Jak do nas dołączyć?">
            <h2></h2>
          </EditableText>

          <div className="cta-text">
            <EditableText textId="jakdolaczyc_tresc" defaultText="Szukamy młodych talentów! Przyjdź na pierwszy trening i spróbuj swoich sił na lodzie.">
              <p></p>
            </EditableText>
          </div>

          {/* Przycisk przewijający do stopki/formularza lub kierujący na inną podstronę */}
          <ContactButton href="/kontakt">
            Skontaktuj się z nami
          </ContactButton>
        </CTASection>

      </AboutContainer>
    </>
  );
};